import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";

const DepotInjectionForm = ({ formData, setFormData, handleSubmit, isLoading = false }) => {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        setRows(
            formData.injectionRecords && formData.injectionRecords.length > 0
                ? formData.injectionRecords
                : [
                    {
                        id: Date.now(),
                        dateTime: "",
                        injectionName: "",
                        mg: "",
                        route: "",
                        duration: "",
                        dueDate: "",
                    },
                ]
        );
    }, [formData.injectionRecords]);

    const handleRowChange = (id, field, value) => {
        const updatedRows = rows.map((r) => {
            if (r.id === id) {
                let newValue = value;

                // 🧭 Convert datetime-local to full ISO string
                if (field === "dateTime" && value) {
                    // Ensures "2025-10-24T12:00" becomes "2025-10-24T12:00:00"
                    newValue = value.length === 16 ? `${value}:00` : value;
                }

                return { ...r, [field]: newValue };
            }
            return r;
        });

        setRows(updatedRows);
        setFormData({ ...formData, injectionRecords: updatedRows });
    };

    const addRow = () => {
        const newRow = {
            id: Date.now(),
            dateTime: "",
            injectionName: "",
            mg: "",
            route: "",
            duration: "",
            dueDate: "",
        };
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        setFormData({ ...formData, injectionRecords: updatedRows });
    };
    const removeRow = (id) => {
        if (rows.length === 1) return;
        Swal.fire({
            title: "Delete this entry?",
            text: "This record will be removed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        }).then((res) => {
            if (res.isConfirmed) {
                const updatedRows = rows.filter((r) => r.id !== id);
                setRows(updatedRows);
                setFormData({ ...formData, injectionRecords: updatedRows });
            }
        });
    };

    // 🖊 Handle row changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <div className="p-4">
            {/* === Header === */}
            <div className="col-12 mb-3">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Client Information
                </small>
            </div>

            {/* === Basic Info === */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="name"
                            value={formData?.name || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Name"
                            required
                            readOnly={!!formData?.patientId || !!formData?.patient_id}
                        />
                        <label>Name</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="mrNo"
                            value={formData?.mrNo || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="MR. No."
                            required
                        />
                        <label>MR. No.</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="date"
                            name="dob"
                            value={formData?.dob || ""}
                            onChange={handleChange}
                            className="form-control"
                            required
                            readOnly={!!formData?.patientId || !!formData?.patient_id}
                        />
                        <label>DOB</label>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="roomNo"
                            value={formData?.roomNo || ""}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Room/Bed No."
                            required
                        />
                        <label>Room / Bed No.</label>
                    </div>
                </div>
            </div>

            {/* === Repeater Section === */}
            <div className="col-12 mb-3">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Injection Records
                </small>
            </div>

            {rows.map((row, index) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 shadow-sm bg-light-subtle">
                    <div className="row g-3 align-items-start">
                        {/* S.No */}
                        <div className="col-md-1 pt-4 d-flex align-items-center justify-content-center fw-bold">
                            {index + 1}
                        </div>

                        {/* Date Time */}
                        <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={row.dateTime || ""}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "dateTime", e.target.value)
                                    }
                                />
                                <label>Date & Time</label>
                            </div>
                        </div>

                        {/* Injection Name */}
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={row.injectionName}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "injectionName", e.target.value)
                                    }
                                />
                                <label>Injection Name</label>
                            </div>
                        </div>

                        {/* MG */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.mg}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "mg", e.target.value)
                                    }
                                />
                                <label>mg</label>
                            </div>
                        </div>

                        {/* Route */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={row.route}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "route", e.target.value)
                                    }
                                />
                                <label>Route</label>
                            </div>
                        </div>

                        {/* Duration */}
                        <div className="col-md-1">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={row.duration}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "duration", e.target.value)
                                    }
                                />
                                <label>Duration</label>
                            </div>
                        </div>

                        {/* Due Date */}
                        <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={row.dueDate}
                                    onChange={(e) =>
                                        handleRowChange(row.id, "dueDate", e.target.value)
                                    }
                                />
                                <label>Due Date</label>
                            </div>
                        </div>

                        {/* Delete Button */}
                        <div className="col-md-1 text-end">
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-lg"
                                onClick={() => removeRow(row.id)}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
            ))}

            {/* Add Row Button */}
            <div className="text-end mt-3">
                <button type="button" className="btn btn-outline-primary" onClick={addRow}>
                    <i className="ri-add-line"></i> Add Row
                </button>
            </div>

        </div>
    );
};

export default DepotInjectionForm;
